import React, { useState, useEffect } from 'react';
import axios from 'axios';


export default function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ssoConfig, setSsoConfig] = useState(null);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [showLocal, setShowLocal] = useState(false);

  useEffect(() => {
    axios.get('/auth/sso-config').then(res => {
      setSsoConfig(res.data);
    }).catch(() => {
      setSsoConfig({ sso_enabled: false });
    });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const returnedState = params.get('state');
    const ssoError = params.get('error');
    const ssoErrorDesc = params.get('error_description');
    const savedState = sessionStorage.getItem('sso_state');
    if (ssoError) {
      setError(ssoErrorDesc || 'SSO authentication failed. Please try again.');
      window.history.replaceState({}, '', '/login');
      return;
    }
    if (code && ssoConfig && ssoConfig.sso_enabled) {
      if (savedState && returnedState !== savedState) {
        setError('SSO state mismatch. Please try again.');
        window.history.replaceState({}, '', '/login');
        return;
      }
      sessionStorage.removeItem('sso_state');
      setSsoLoading(true);
      setError('');
      const ssoRedirectUri = ssoConfig.redirect_uri || (window.location.origin + '/login');
      axios.post('/auth/sso-callback', {
        code,
        redirect_uri: ssoRedirectUri
      }).then(res => {
        localStorage.setItem('lz_token', res.data.access_token);
        localStorage.setItem('lz_username', res.data.username);
        localStorage.setItem('lz_auth_method', 'azure_ad');
        window.history.replaceState({}, '', '/login');
        onLogin(res.data);
      }).catch(err => {
        setError(err.response?.data?.detail || 'SSO authentication failed');
        window.history.replaceState({}, '', '/login');
      }).finally(() => setSsoLoading(false));
    }
  }, [ssoConfig, onLogin]);

  const handleLocalLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post('/auth/login', { username, password });
      localStorage.setItem('lz_token', res.data.access_token);
      localStorage.setItem('lz_username', res.data.username);
      localStorage.setItem('lz_auth_method', 'local');
      onLogin(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSSOLogin = () => {
    if (!ssoConfig || !ssoConfig.sso_enabled) {
      setError('Microsoft SSO is not configured. Please contact your administrator to set up Azure AD integration.');
      return;
    }
    const state = Math.random().toString(36).substring(2) + Date.now().toString(36);
    sessionStorage.setItem('sso_state', state);
    const ssoRedirectUri = ssoConfig.redirect_uri || (window.location.origin + '/login');
    const params = new URLSearchParams({
      client_id: ssoConfig.client_id,
      response_type: 'code',
      redirect_uri: ssoRedirectUri,
      scope: 'openid profile email User.Read',
      response_mode: 'query',
      state: state,
    });
    window.location.href = `${ssoConfig.authority}/oauth2/v2.0/authorize?${params.toString()}`;
  };

  if (ssoLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.logoSection}>
            <img src="/kgs-logo.png" alt="Kaizentrix Global Solutions" style={styles.logo} />
          </div>
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <div style={styles.spinner}></div>
            <p style={{ color: '#6b7280', marginTop: '1rem' }}>Completing sign-in with Microsoft...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logoSection}>
          <img src="/kgs-logo.png" alt="Kaizentrix Global Solutions" style={styles.logo} />
        </div>

        <div style={styles.headerSection}>
          <h1 style={styles.title}>Landing Zone Intelligence</h1>
          <p style={styles.subtitle}>Cloud Governance & Assessment Platform</p>
        </div>

        <button onClick={handleSSOLogin} style={styles.ssoButton}>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          <span>Sign in with SSO</span>
        </button>

        {error && (
          <div style={styles.error}>
            <span style={{ marginRight: 6 }}>&#9888;</span>
            {error}
          </div>
        )}

        <div style={styles.footer}>
          <p style={styles.footerText}>Powered by Kaizentrix Global Solutions</p>
          <button
            type="button"
            onClick={() => setShowLocal(v => !v)}
            style={styles.localToggle}
          >
            {showLocal ? 'Hide local sign-in' : 'Sign in with local credentials'}
          </button>
          {showLocal && (
            <form onSubmit={handleLocalLogin} style={styles.localForm}>
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                style={styles.localInput}
                placeholder="Username"
                required
                autoComplete="username"
              />
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                style={styles.localInput}
                placeholder="Password"
                required
                autoComplete="current-password"
              />
              <button type="submit" disabled={loading} style={styles.localBtn}>
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #1a1f36 0%, #2d1b69 50%, #1a1f36 100%)',
    padding: '1rem',
  },
  card: {
    background: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    padding: '2.5rem',
    width: '100%',
    maxWidth: '420px',
  },
  logoSection: {
    textAlign: 'center',
    marginBottom: '1.25rem',
    padding: '0.5rem 0 1rem 0',
    borderBottom: '1px solid #f0f0f0',
  },
  logo: {
    height: '140px',
    objectFit: 'contain',
  },
  headerSection: {
    textAlign: 'center',
    marginBottom: '1.75rem',
  },
  title: {
    fontSize: '1.35rem',
    fontWeight: 700,
    color: '#1a1f36',
    margin: 0,
    lineHeight: 1.3,
  },
  subtitle: {
    fontSize: '0.85rem',
    color: '#6b7280',
    margin: '0.35rem 0 0 0',
  },
  ssoButton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    padding: '0.85rem 1rem',
    border: 'none',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #6c5ce7, #5b4cdb)',
    fontSize: '1rem',
    fontWeight: 600,
    color: '#ffffff',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  error: {
    padding: '0.65rem 0.85rem',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    color: '#dc2626',
    fontSize: '0.85rem',
    fontWeight: 500,
    marginTop: '1rem',
  },
  footer: {
    textAlign: 'center',
    marginTop: '2rem',
    paddingTop: '1rem',
    borderTop: '1px solid #f3f4f6',
  },
  footerText: {
    fontSize: '0.75rem',
    color: '#9ca3af',
    margin: '0 0 0.5rem 0',
  },
  localToggle: {
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    fontSize: '0.7rem',
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: '0.25rem',
  },
  localForm: {
    marginTop: '0.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
  },
  localInput: {
    width: '100%',
    padding: '0.45rem 0.6rem',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '0.8rem',
    outline: 'none',
    boxSizing: 'border-box',
  },
  localBtn: {
    width: '100%',
    padding: '0.45rem',
    background: '#6b7280',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid #e5e7eb',
    borderTopColor: '#6c5ce7',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto',
  },
};
